/**
 * Reading a wedding's editing progress from its deliverables and their
 * checkpoints (migration 20261025130000). Pure, so the editing queue and the
 * wedding page say the same thing.
 */

interface Step {
  title: string
  done_at: string | null
  sort_order: number
}

interface Item {
  title: string
  kind: 'photo' | 'film' | 'album' | 'other'
  internal: boolean
  done_at: string | null
  sort_order: number
  steps: Step[]
}

const byOrder = <T extends { sort_order: number }>(a: T, b: T) => a.sort_order - b.sort_order

/** Photo work is everything that isn't film: galleries, sneak peeks, albums. */
export function inQueue(item: Pick<Item, 'kind'>, queue: 'photos' | 'film'): boolean {
  return queue === 'film' ? item.kind === 'film' : item.kind !== 'film'
}

/**
 * The next thing to do in one queue: files and backups first, then each
 * deliverable in order, its first unticked step. Null when all is done.
 */
export function nextStep(items: Item[], queue: 'photos' | 'film'): { item: string; step: string | null } | null {
  const ordered = items
    .filter((item) => inQueue(item, queue))
    .sort((a, b) => Number(b.internal) - Number(a.internal) || byOrder(a, b))
  for (const item of ordered) {
    if (item.steps.length === 0) {
      if (!item.done_at) return { item: item.title, step: null }
      continue
    }
    const open = [...item.steps].sort(byOrder).find((step) => !step.done_at)
    if (open) return { item: item.title, step: open.title }
  }
  return null
}

/** Steps ticked out of all steps in one queue, counting a step-less item as one. */
export function queueProgress(items: Item[], queue: 'photos' | 'film'): { done: number; total: number } {
  let done = 0
  let total = 0
  for (const item of items.filter((i) => inQueue(i, queue))) {
    if (item.steps.length === 0) {
      total += 1
      if (item.done_at) done += 1
    } else {
      total += item.steps.length
      done += item.steps.filter((step) => step.done_at).length
    }
  }
  return { done, total }
}

// ---------------------------------------------------------------------------
// Delivered, and when it is due (27 Oct 2026)
// ---------------------------------------------------------------------------

const DELIVERED_STEP = /(deliver|sent)/i

/**
 * Delivered to the couple: done, or its "Delivered"/"Sent" step ticked (the
 * "Backed up" after it doesn't count). Same rule as job_deliveries() in SQL.
 */
export function isDelivered(item: Pick<Item, 'done_at' | 'steps'>): boolean {
  return Boolean(item.done_at) || item.steps.some((step) => step.done_at && DELIVERED_STEP.test(step.title))
}

/**
 * The steps to tick when it is marked delivered: everything up to and
 * including the "Delivered"/"Sent" step (or every step if it has none of
 * those). Unticking clears that step and the ones after it.
 */
export function stepsForDelivered<S extends Step & { id: string }>(steps: S[], delivered: boolean): S[] {
  const ordered = [...steps].sort(byOrder)
  const index = ordered.findIndex((step) => DELIVERED_STEP.test(step.title))
  const cut = index === -1 ? ordered.length - 1 : index
  return delivered ? ordered.slice(0, cut + 1) : ordered.slice(cut)
}

/**
 * When a deliverable is due, from Jena's turnaround (27 Oct 2026): sneak peeks
 * 3 days after the wedding, the full gallery 8 weeks, the trailer 2 weeks,
 * the highlight film 12 weeks. A due date set on the wedding (photos or film)
 * wins for the gallery and the highlight film. Engagement shoots, albums and
 * extras have no date here.
 */
export function deliverableDue(
  item: Pick<Item, 'title' | 'kind'>,
  job: { event_date: string; photos_due_date: string | null; film_due_date: string | null },
): string | null {
  const after = (days: number) => {
    const date = new Date(`${job.event_date}T12:00:00Z`)
    date.setUTCDate(date.getUTCDate() + days)
    return date.toISOString().slice(0, 10)
  }
  if (/engagement/i.test(item.title)) return null
  if (item.kind === 'photo') {
    if (/(sneak|preview)/i.test(item.title)) return after(3)
    return job.photos_due_date ?? after(56)
  }
  if (item.kind === 'film') {
    if (/trailer|teaser/i.test(item.title)) return after(14)
    return job.film_due_date ?? after(84)
  }
  return null
}
